import { Iteration } from '@deltek/specification-client/core/Models/productListingDetail'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import { makeStyles, Theme } from '@material-ui/core/styles'
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline'
import Skeleton from '@material-ui/lab/Skeleton'
import clsx from 'clsx'
import { BulkAddElementInput } from 'models/getProjectElementNodesInput'
import { ImportUploadFileInfo } from 'models/projectImport'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import React from 'react'
import { useLocation } from 'react-router-dom'
import { VariableSizeNodeComponentProps } from 'react-vtree'
import { AreaContext } from '../Common/AreaContext'
import { onEnterKeyDown } from '../Common/utils'
import ElementItemCard from './ElementItemCard'
import { ElementItemDataNode } from './ElementTreeView'

interface ElementTreeStyleProps {
    contentTeamFromStudioPage: boolean | undefined
    isBetaWorkspaceToggleEnabled?: boolean
    loadingSkeletonIndex?: number
}

const useElementTreeViewNodeStyles = makeStyles((theme: Theme) => ({
    loadMoreButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(2),
        marginTop: theme.spacing(1),
    },
    cardContainer: {
        padding: (props: ElementTreeStyleProps) =>
            props.isBetaWorkspaceToggleEnabled
                ? theme.spacing(0.5, 0.5, 0, 1)
                : theme.spacing(1),
        paddingTop: 0,
        paddingRight: 16,
    },
    loadingContainerImport: {
        padding: 8,
        paddingTop: 0,
        paddingRight: 16,
        width: 300,
    },
    cardContainerImport: {
        '& .MuiSvgIcon-root': {
            height: 20,
            width: 20,
        },
        padding: theme.spacing(1),
        paddingTop: 0,
        paddingRight: theme.spacing(2),
        // display: 'flex',
        paddingLeft: theme.spacing(2),
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '100%',
    },
    loadSkeleton: {
        borderRadius: 4,
        backgroundColor: (props: ElementTreeStyleProps) =>
            props.contentTeamFromStudioPage ? '#7288a3' : '#dedede',
    },
    loadingSkeletonRoot: {
        borderRadius: 4,
        backgroundColor: (props: ElementTreeStyleProps) =>
            props.loadingSkeletonIndex === 0
                ? '#E1E6F1'
                : props.loadingSkeletonIndex === 1
                ? '#DAE1EF'
                : '#CED8EC',
    },

    loadingSkeletonChildElement: {
        borderRadius: 4,
        backgroundColor: '#CED8EC',
    },
    loadingDivision: {
        borderRadius: 4,
        marginRight: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    loadMoreButton: {
        borderRadius: 100,
        backgroundColor: '#304959',
        padding: theme.spacing(0.375, 2.5),
        fontSize: pxToRem(12),
        color: theme.palette.common.white,
        '&:hover': {
            backgroundColor: '#091B32',
        },
    },
    importChecked: {
        color: '#33B55B',
        marginLeft: theme.spacing(4),
        position: 'absolute',
        right: -40,
        cursor: 'pointer',
        bottom: 16,
    },
    parentChecked: {
        color: '#33B55B',
        marginLeft: theme.spacing(4),
        position: 'absolute',
        right: -40,
        bottom: 16,
    },
    parentUnChecked: {
        color: 'grey',
        marginLeft: theme.spacing(4),
        position: 'absolute',
        right: -40,
        bottom: 16,
    },
    importUnChecked: {
        color: 'grey',
        marginLeft: theme.spacing(4),
        position: 'absolute',
        right: -40,
        cursor: 'pointer',
        bottom: 16,
    },
    duplicateImportChecked: {
        color: '#33B55B',
        marginLeft: theme.spacing(4),
        position: 'absolute',
        right: -40,
        cursor: 'pointer',
        top: '50%',
        transform: 'translate(0, -50%)',
    },
    duplicateImportUnChecked: {
        color: 'grey',
        marginLeft: theme.spacing(4),
        position: 'absolute',
        right: -40,
        cursor: 'pointer',
        top: '50%',
        transform: 'translate(0, -50%)',
    },
}))
export interface TreeNodeProps extends ElementItemDataNode {
    id: string
    nestingLevel: number
    onClick?: (element: ElementItemDataNode) => void | VoidFunction
    onBulkSelect?: (
        element: ElementItemDataNode,
        type: string,
        isAdd: boolean
    ) => void | VoidFunction
    handleBPElementTreeChange?: (
        id: string,
        treepath: string,
        isBP: boolean,
        isGBP: boolean,
        remove: boolean
    ) => void
    isOpenByDefault: boolean
    defaultHeight: number
    defaultWidth: number | string
    isCurrentlyExpanded?: boolean
    searchText?: string
    isPartOfSearchResult?: boolean
    loadChildren: (id: string, isBulkSelectRequested?: boolean) => void
    calledFromImportPage?: boolean
    isLastSubAssembly?: boolean
    isBestPractice?: boolean
    isGlobalBestPractice?: boolean
    isProjectOnly?: boolean
    isImportedToProject?: boolean
    isBpPublished?: boolean
    isBasedFromImportedNode?: boolean
    importedToProjectId?: string
    selectedFile?: Partial<ImportUploadFileInfo> | null
    isAddedForAssociation?: boolean
    hasChildAssociation?: boolean
    hasSiblingAssociation?: boolean
    projectId?: string
    contentTeamFromStudioPage?: boolean
    disableExpand?: boolean
    selectedBulkAddElements?: BulkAddElementInput[]
    elementType?: string
    isFromWorkSpacePage?: boolean
    calledFromImportDuplicateFamily?: boolean
    isDuplicateFamilyVersionSelected?: boolean
    elementAddedToProjectId?: string
    /**TODO: This is the temporary code changes for CKE ProductType elements once it will be available we can remove some of the conditions */
    parentFamilyId?: string
    iteration?: Iteration
    isDeactivated?: boolean
    isBetaWorkspaceToggleEnabled?: boolean
}

const ElementTreeViewNode: React.FC<VariableSizeNodeComponentProps<
    TreeNodeProps
>> = ({ data, isOpen, style, toggle, resize }) => {
    const { pathname } = useLocation()
    const { projectId } = React.useContext(ProjectPageContext)

    const { persistState } = React.useContext(AreaContext)
    const isBetaWorkspaceToggleEnabled =
        data.isBetaWorkspaceToggleEnabled ?? false
    const classes = useElementTreeViewNodeStyles({
        contentTeamFromStudioPage: data.contentTeamFromStudioPage,
        isBetaWorkspaceToggleEnabled,
        loadingSkeletonIndex: Number(
            data.id.substring(data.id.indexOf('-') + 1)
        ),
    })

    const currentElementCount = data.id.split('|')?.[1]
    const totalElementCount = data.id.split('|')?.[2]
    const isDivisionLoading = React.useMemo(() => {
        return (
            data?.selectedElementFormat === 'DIVISIONS' &&
            (data?.title?.length === 0 || !data?.title)
        )
    }, [data])

    const loadingSkeletonStylings = React.useMemo(() => {
        const childSkeletonLoading =
            data?.parentNodeId === 'loadingSkeletonParent'
        if (isBetaWorkspaceToggleEnabled) {
            return childSkeletonLoading
                ? classes.loadingSkeletonChildElement
                : classes.loadingSkeletonRoot
        } else {
            return isDivisionLoading
                ? classes.loadingDivision
                : classes.loadSkeleton
        }
    }, [
        data?.parentNodeId,
        isDivisionLoading,
        isBetaWorkspaceToggleEnabled,
        classes,
    ])
    const isItemInBulkAdd = React.useMemo(() => {
        return data.selectedBulkAddElements?.some(
            (item) =>
                item.nodeId === data.elementId || item.treePath === data.id
        )
    }, [data])

    const hasChildrenOtherThanAssociation: ElementItemDataNode[] = data?.calledFromImportPage
        ? data?.children?.filter(
              (itemData) =>
                  itemData?.title !== data?.selectedFile?.title &&
                  itemData?.elementType === 'PRODUCTFAMILY'
          ) ?? []
        : []
    const handleItemCardClick = React.useCallback(() => {
        data.onClick?.(data)
        persistState?.('selectedTopicData', undefined)
        if (
            data.contentTeamFromStudioPage &&
            data.isLastSubAssembly &&
            !pathname.includes('workspace')
        ) {
            data.loadChildren(data.elementId as string)
        } else if (!isOpen) {
            if (
                (data?.hasSubItems &&
                    (data?.children?.length === 0 || !data?.children)) ||
                (data?.calledFromImportPage &&
                    data?.hasSubItems &&
                    hasChildrenOtherThanAssociation?.length === 0 &&
                    data?.isLastSubAssembly) ||
                data?.calledFromImportDuplicateFamily
            ) {
                data.loadChildren(data.elementId as string)
            }
            toggle()
        }
    }, [data, toggle, isOpen, persistState, pathname])

    const handleBulkAddClick = React.useCallback(
        (type: string, isAdd: boolean) => {
            data.onBulkSelect?.(data, type, isAdd)
        },
        [data]
    )

    const handleRequestChildrenForBulkAdd = React.useCallback(() => {
        data.loadChildren(`${data.elementId}`, true)
    }, [data])

    const handleCardExpand = React.useCallback(() => {
        if (!isOpen) {
            if (
                (data?.hasSubItems &&
                    (data?.children?.length === 0 || !data?.children)) ||
                (data?.calledFromImportPage &&
                    data?.hasSubItems &&
                    hasChildrenOtherThanAssociation?.length === 0 &&
                    data?.isLastSubAssembly)
            ) {
                data.loadChildren(
                    data?.selectedElementFormat === 'DIVISIONS'
                        ? data?.id
                        : (data.elementId as string)
                )
            }
            toggle()
        }
    }, [data, toggle, isOpen])
    const handleItemCardCollapse = React.useCallback(
        (event: React.MouseEvent<SVGSVGElement>) => {
            event.stopPropagation()

            if (isOpen) {
                toggle()
            } else {
                if (data?.hasSubItems && data?.children?.length === 0) {
                    data.loadChildren(
                        data?.selectedElementFormat === 'DIVISIONS'
                            ? data?.id
                            : (data.elementId as string)
                    )
                }
            }
        },
        [data, toggle, isOpen]
    )
    React.useEffect(() => {
        resize(data.defaultHeight)
    }, [data, resize])

    if (
        data.id.startsWith('loadSkeleton') ||
        data.elementId?.startsWith('loadSkeleton') ||
        isDivisionLoading
    ) {
        const index = Number(data.id.substring(data.id.indexOf('-') + 1))
        return (
            <div
                style={{
                    ...style,

                    opacity: isBetaWorkspaceToggleEnabled
                        ? 1
                        : 0.6 - index * 0.2,
                    marginLeft:
                        data.nestingLevel *
                        (isBetaWorkspaceToggleEnabled ? 10 : 16),
                    width: data.contentTeamFromStudioPage
                        ? `calc(100% - ${data.nestingLevel * 16}px)`
                        : 'inherit',
                }}
                className={clsx(
                    'elementCardLoadSkeleton',
                    data.calledFromImportPage
                        ? classes.loadingContainerImport
                        : classes.cardContainer
                )}
            >
                <Skeleton
                    variant="rect"
                    animation="pulse"
                    height="100%"
                    className={loadingSkeletonStylings}
                />
            </div>
        )
    }

    return (
        <div
            style={{
                ...style,
                width: data.calledFromImportPage
                    ? 370
                    : data.contentTeamFromStudioPage ||
                      isBetaWorkspaceToggleEnabled
                    ? '100%'
                    : 326,
            }}
        >
            {data.id.startsWith('loadMoreButton') ? (
                <div className={classes.loadMoreButtonContainer}>
                    <Button
                        className={classes.loadMoreButton}
                        data-testid="elementListLoadMoreButton"
                        onClick={() => (data.onClick as VoidFunction)?.()}
                    >
                        Load More ({(+currentElementCount).toLocaleString('en')}{' '}
                        of {(+totalElementCount).toLocaleString('en')})
                    </Button>
                </div>
            ) : (
                <div
                    className={
                        data.calledFromImportPage
                            ? classes.cardContainerImport
                            : classes.cardContainer
                    }
                    style={{
                        marginLeft:
                            data.nestingLevel *
                            (isBetaWorkspaceToggleEnabled ? 10 : 16),
                        width: data.contentTeamFromStudioPage
                            ? `calc(405px - ${data.nestingLevel * 16})`
                            : 'auto',
                    }}
                >
                    <ElementItemCard
                        handleCollapse={handleItemCardCollapse}
                        onClick={handleItemCardClick}
                        onRequestChildrenForBulkAdd={
                            handleRequestChildrenForBulkAdd
                        }
                        onBulkSelect={handleBulkAddClick}
                        handleCardExpand={handleCardExpand}
                        handleBPElementTreeChange={
                            data?.handleBPElementTreeChange
                        }
                        id={data.id}
                        isSelected={data.selected}
                        isInProject={data.isInProject}
                        isInBulkAdd={isItemInBulkAdd}
                        //TODO: Pass in inactive/active prop here
                        inactive={false}
                        title={data.title}
                        subtitle={data.subtitle}
                        expanded={isOpen}
                        hasSubItems={data.hasSubItems}
                        areSubItemsLoaded={
                            data.hasSubItems &&
                            data.children &&
                            data.children.length > 0
                        }
                        searchText={data.searchText}
                        isSearchResult={data.isSearchResult ?? false}
                        isL4L5Assembly={data.isL4L5Assembly}
                        defaultWidth={data.defaultWidth}
                        calledFromImportPage={data.calledFromImportPage}
                        isSelectedForAssociation={data.isSelectedForAssociation}
                        isLastSubAssembly={data.isLastSubAssembly}
                        isBestPractice={data.isBestPractice}
                        isGlobalBestPractice={data.isGlobalBestPractice}
                        isProjectOnly={data.isProjectOnly}
                        isImportedToProject={
                            data?.isBpPublished
                                ? true
                                : data?.isBasedFromImportedNode
                                ? data?.isBasedFromImportedNode
                                : data?.isImportedToProject &&
                                  data?.importedToProjectId === projectId
                        }
                        isAddedForAssociation={data.isAddedForAssociation}
                        elementId={data.elementId}
                        contentTeamFromStudioPage={
                            data.contentTeamFromStudioPage
                        }
                        selectedElementFormat={data.selectedElementFormat}
                        disableExpand={data.disableExpand}
                        isBasedFromManufacturerOption={
                            data.isBasedFromManufacturerOption
                        }
                        isBPPublished={data?.isBpPublished}
                        isProjectImported={
                            data?.isImportedToProject &&
                            data?.importedToProjectId === projectId
                        }
                        elementType={data.elementType}
                        isFromWorkSpacePage={data.isFromWorkSpacePage}
                        isDivision={
                            data?.elementType === 'ASSEMBLY' && // "assemblies" in Divisions view are actually divisions
                            data?.selectedElementFormat === 'DIVISIONS'
                        }
                        calledFromImportDuplicateFamily={
                            data.calledFromImportDuplicateFamily
                        }
                        baseElementId={data.baseElementId}
                        updateElementList={data.updateElementList}
                        parentNodeId={data.parentNodeId}
                        elementAddedToProjectId={data.elementAddedToProjectId}
                        tabIndex={
                            data.calledFromImportDuplicateFamily &&
                            data.nestingLevel > 0
                                ? 0
                                : -1
                        }
                        currentElementTreePath={data.treePath}
                        /**TODO: This is the temporary code changes for CKE ProductType elements once it will be available we can remove some of the conditions */
                        parentFamilyId={data.parentFamilyId}
                        currentIteration={data?.iteration}
                        nestingLevel={data.nestingLevel}
                        isDeactivated={data?.isDeactivated}
                        isFromPPLTemplate={data?.isFromPPLTemplate}
                        isBetaWorkspaceToggleEnabled={
                            isBetaWorkspaceToggleEnabled
                        }
                    />
                    {data.calledFromImportPage &&
                        data.isLastSubAssembly &&
                        data.elementType === 'ASSEMBLY' && (
                            <CheckCircleOutline
                                onClick={handleItemCardClick}
                                onKeyDown={onEnterKeyDown(handleItemCardClick)}
                                className={
                                    data.isSelectedForAssociation
                                        ? classes.importChecked
                                        : classes.importUnChecked
                                }
                                tabIndex={0}
                            />
                        )}
                    {(!data.isLastSubAssembly || data?.hasSiblingAssociation) &&
                        data.elementType === 'ASSEMBLY' && (
                            <CheckCircleOutline
                                className={
                                    data.hasChildAssociation ||
                                    data?.hasSiblingAssociation
                                        ? classes.parentChecked
                                        : classes.parentUnChecked
                                }
                            />
                        )}

                    {data.calledFromImportDuplicateFamily && (
                        <CheckCircleOutline
                            onClick={handleItemCardClick}
                            onKeyDown={onEnterKeyDown(handleItemCardClick)}
                            className={
                                data.isDuplicateFamilyVersionSelected
                                    ? classes.duplicateImportChecked
                                    : classes.duplicateImportUnChecked
                            }
                            tabIndex={0}
                        />
                    )}
                </div>
            )}
        </div>
    )
}

export default ElementTreeViewNode
